import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { SiFitbit } from 'react-icons/si';
import { IoWatch, IoFitness, IoHeart } from 'react-icons/io5';
import { GiRing } from 'react-icons/gi';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import type { Notification } from '@/lib/notifications/types';

interface WearableReauthNotificationProps {
  notification: Notification;
  onMarkAsRead?: (notificationId: string) => void;
  onReconnect?: () => void;
  className?: string;
}

const getProviderIcon = (provider: string) => {
  switch (provider.toLowerCase()) {
    case 'whoop':
      return <IoHeart className="w-4 h-4 text-red-600" />;
    case 'oura':
      return <GiRing className="w-4 h-4 text-blue-600" />;
    case 'fitbit':
      return <SiFitbit className="w-4 h-4 text-green-600" />;
    case 'biostrap':
      return <IoFitness className="w-4 h-4 text-purple-600" />;
    default:
      return <IoWatch className="w-4 h-4 text-gray-600" />;
  }
};



export const WearableReauthNotification: React.FC<WearableReauthNotificationProps> = ({
  notification,
  onMarkAsRead,
  onReconnect,
  className
}) => {
  const [isReconnecting, setIsReconnecting] = useState(false);
  const navigate = useNavigate();
  
  const provider = notification.data?.provider || 'device';
  const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
  
  const handleReconnect = async () => {
    setIsReconnecting(true);
    
    try {
      // Mark notification as read first
      if (onMarkAsRead) {
        onMarkAsRead(notification.id);
      }

      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        console.error('❌ User not authenticated:', authError);
        toast({
          title: 'Authentication Required',
          description: 'Please log in to reconnect your wearable device',
          variant: 'destructive',
        });
        return;
      }

      // Call wearable-auth function to get the OAuth URL
      const { data, error } = await supabase.functions.invoke('wearable-auth', {
        body: { 
          provider: provider,
          onboarding_context: 'false'
        }
      });

      if (error) {
        console.error('❌ Connection error:', error);
        throw error;
      }

      console.log('✅ Connection response:', data);

      if (data?.url) {
        console.log('🌐 Redirecting to OAuth URL:', data.url);
        window.location.href = data.url;
      } else {
        throw new Error('No OAuth URL returned');
      }
      
      // Call custom callback if provided
      if (onReconnect) {
        onReconnect();
      }
    } catch (error: any) {
      console.error('💥 Reconnection failed:', error);

      let errorMessage = 'Failed to initiate reconnection';
      if (error?.message) {
        errorMessage = error.message;
      }
      if (error?.error?.message) {
        errorMessage = error.error.message;
      }

      toast({
        title: 'Reconnection Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsReconnecting(false);
    }
  };

  const handleDismiss = () => {
    if (onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <div className={cn(
      'bg-red-50 border border-red-200 rounded-md px-3 py-2',
      className
    )}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="flex items-center justify-center w-6 h-6 bg-white rounded-full shadow-sm flex-shrink-0">
            {getProviderIcon(provider)}
          </div>
          <span className="text-xs font-medium text-red-800 truncate">
            {providerName} expired
          </span>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <Button 
            onClick={handleReconnect}
            disabled={isReconnecting}
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white h-6 px-2 text-xs"
          >
            {isReconnecting ? (
              <RefreshCw className="h-3 w-3 animate-spin" />
            ) : (
              'Fix'
            )}
          </Button>
          <Button 
            variant="ghost"
            onClick={handleDismiss}
            disabled={isReconnecting}
            size="sm"
            className="text-red-600 hover:bg-red-100 h-6 w-6 p-0 text-sm"
          >
            ×
          </Button>
        </div>
      </div>
    </div>
  );
};

